import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  MousePointerClick,
  Package,
  Plus,
  Radio,
  SearchX,
  ShoppingBag,
} from 'lucide-react';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';
import { useLanguage } from '@/i18n';
import CloudflareAnalytics from '@/components/dashboard/CloudflareAnalytics';
import { AnalyticsAgentRow, AnalyticsComparisonWindow, AnalyticsOverview, compareMetricValues } from '@/services/analyticsMetrics';
import {
  FirstPartyAnalyticsRange,
  getAgentAnalytics,
  getFirstPartyAnalyticsOverviewComparison,
} from '@/services/analyticsService';
import {
  AnalyticsErrorState,
  AnalyticsLoadingState,
  AnalyticsRangeTabs,
  MetricCard,
} from '@/components/analytics/AnalyticsShared';
import { formatDeltaText, formatNumber, formatPercent, formatUtcRange } from '@/components/analytics/analyticsDisplay';

type ClothingRow = Database['public']['Tables']['db']['Row'];
type DashboardRow = ClothingRow & {
  id?: string | number;
  _id?: string | number;
};

type BaseItem = {
  id?: string | number;
  _id?: string | number;
  name: string;
  image: string;
  price: number;
  brand: string;
  batch: string;
  category: string | null;
  created_at: string;
  created_by: string;
  link: string;
  updated_at: string;
};

interface RecentItem extends BaseItem {
  type: 'clothing';
  itemUrl: string;
}

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalItems: 0,
  });
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [analyticsRange, setAnalyticsRange] = useState<FirstPartyAnalyticsRange>('7d');
  const [analyticsOverview, setAnalyticsOverview] = useState<AnalyticsOverview | null>(null);
  const [analyticsPreviousOverview, setAnalyticsPreviousOverview] = useState<AnalyticsOverview | null>(null);
  const [analyticsComparisonWindow, setAnalyticsComparisonWindow] = useState<AnalyticsComparisonWindow | null>(null);
  const [agentRows, setAgentRows] = useState<AnalyticsAgentRow[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const { t } = useLanguage();

  const fetchCatalogStats = useCallback(async () => {
    try {
      setCatalogLoading(true);
      const { count: totalClothing } = await supabase
        .from('db')
        .select('*', { count: 'exact', head: true });

      let recentClothing: ClothingRow[] | null = null;
      let recentClothingError: unknown = null;
      const attempt = await supabase.from('db').select('*').order('id', { ascending: false }).limit(6);
      if (attempt.error) {
        const attempt2 = await supabase.from('db').select('*').order('_id', { ascending: false }).limit(6);
        recentClothing = attempt2.data as unknown as ClothingRow[] | null;
        recentClothingError = attempt2.error;
      } else {
        recentClothing = attempt.data as unknown as ClothingRow[] | null;
      }

      if (recentClothingError) {
        console.error('Error fetching recent db items:', recentClothingError);
        toast.error(t('dashboardCatalogLoadFailed'));
        return;
      }

      setStats({
        totalItems: totalClothing || 0,
      });

      setRecentItems(
        recentClothing?.map((item) => {
          const row = item as DashboardRow;
          const itemId = row.id ?? row._id;
          return {
            ...row,
            type: 'clothing' as const,
            itemUrl: `/admin/clothinglist/${itemId}`,
          };
        }) || [],
      );
    } catch (error) {
      console.error('Error fetching dashboard catalog data:', error);
      toast.error(t('dashboardCatalogLoadFailed'));
    } finally {
      setCatalogLoading(false);
    }
  }, [t]);

  const fetchBusinessAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      const [overviewResult, agentsResult] = await Promise.all([
        getFirstPartyAnalyticsOverviewComparison(analyticsRange),
        getAgentAnalytics(analyticsRange),
      ]);

      setAnalyticsOverview(overviewResult.current);
      setAnalyticsPreviousOverview(overviewResult.previous);
      setAnalyticsComparisonWindow(overviewResult.window);
      setAgentRows(agentsResult);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('analyticsLoadFailed');
      setAnalyticsError(message);
      toast.error(message);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsRange, t]);

  useEffect(() => {
    fetchCatalogStats();
  }, [fetchCatalogStats]);

  useEffect(() => {
    fetchBusinessAnalytics();
  }, [fetchBusinessAnalytics]);

  const topClickedAgent = agentRows[0]?.agent || '--';
  const topSelectedAgent = useMemo(
    () => [...agentRows].sort((a, b) => b.selections - a.selections || a.agent.localeCompare(b.agent))[0]?.agent || '--',
    [agentRows],
  );
  const comparisonLabel = t('analyticsComparedWithPrior');

  const feedStatusLabel = analyticsOverview ? t(`analyticsFeedStatus${capitalize(analyticsOverview.feedStatus)}`) : '--';
  const feedStatusDescription = analyticsOverview
    ? t(`analyticsFeedStatus${capitalize(analyticsOverview.feedStatus)}Description`)
    : t('analyticsFeedStatusUnknownDescription');

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-1">{t('dashboard')}</h1>
          <p className="text-muted-foreground">{t('welcome')}</p>
        </div>
        <Link to="/admin/clothinglist/new">
          <Button className="flex items-center gap-2">
            <Plus size={16} />
            {t('addProduct')}
          </Button>
        </Link>
      </div>

      <section className="space-y-4" aria-labelledby="business-overview-heading">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 id="business-overview-heading" className="text-xl font-semibold tracking-tight">
              {t('analyticsBusinessOverview')}
            </h2>
            <p className="text-sm text-muted-foreground">{t('analyticsBusinessOverviewDescription')}</p>
            {analyticsOverview?.start && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('analyticsUtcRange', { range: formatUtcRange(analyticsOverview.start, analyticsOverview.end) })}
              </p>
            )}
            {analyticsComparisonWindow?.previous && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('analyticsPriorUtcRange', { range: formatUtcRange(analyticsComparisonWindow.previous.start, analyticsComparisonWindow.previous.end) })}
              </p>
            )}
          </div>
          <AnalyticsRangeTabs
            range={analyticsRange}
            onRangeChange={setAnalyticsRange}
            rangeLabel={t('analyticsDateRange')}
          />
        </div>

        {analyticsLoading ? (
          <AnalyticsLoadingState label={t('analyticsLoading')} />
        ) : analyticsError ? (
          <AnalyticsErrorState
            title={t('analyticsErrorTitle')}
            message={analyticsError}
            retryLabel={t('analyticsRetry')}
            onRetry={fetchBusinessAnalytics}
          />
        ) : analyticsOverview ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <MetricCard
              title={t('analyticsAffiliateClicks')}
              value={formatNumber(analyticsOverview.affiliateClicks)}
              description={t('analyticsAffiliateClicksDescription')}
              icon={MousePointerClick}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(analyticsOverview.affiliateClicks, analyticsPreviousOverview?.affiliateClicks), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsTopClickedAgent')}
              value={topClickedAgent}
              description={t('analyticsTopClickedAgentDescription')}
              icon={Radio}
            />
            <MetricCard
              title={t('analyticsTopSelectedAgent')}
              value={topSelectedAgent}
              description={t('analyticsTopSelectedAgentDescription')}
              icon={Activity}
            />
            <MetricCard
              title={t('analyticsZeroResultRate')}
              value={formatPercent(analyticsOverview.zeroResultRate)}
              description={t('analyticsZeroResultRateDescription')}
              icon={SearchX}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(analyticsOverview.zeroResultRate, analyticsPreviousOverview?.zeroResultRate), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsFeedStatus')}
              value={feedStatusLabel}
              description={feedStatusDescription}
              icon={AlertTriangle}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(analyticsOverview.productFeedFailures, analyticsPreviousOverview?.productFeedFailures), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
          </div>
        ) : null}
      </section>

      <CloudflareAnalytics />

      <section className="space-y-4" aria-labelledby="catalog-overview-heading">
        <div>
          <h2 id="catalog-overview-heading" className="text-xl font-semibold tracking-tight">
            {t('analyticsCatalogOverview')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('analyticsCatalogOverviewDescription')}</p>
        </div>

        {catalogLoading ? (
          <div className="grid place-items-center h-60 bg-card rounded-lg border">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
              <p className="text-sm text-muted-foreground">{t('dashboardLoading')}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4">
              <Card className="overflow-hidden border-l-4 border-l-primary bg-gradient-to-br from-card to-background">
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-muted-foreground mb-1">{t('totalDb')}</p>
                      <p className="text-3xl font-bold">{stats.totalItems}</p>
                    </div>
                    <div className="rounded-full bg-primary/10 p-3">
                      <ShoppingBag className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Card className="overflow-hidden">
                <CardHeader className="border-b bg-card/50">
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-primary" />
                        {t('recentDb')}
                      </CardTitle>
                      <CardDescription>{t('latestDb')}</CardDescription>
                    </div>
                    <Button asChild size="sm" variant="outline">
                      <Link to="/admin/clothinglist">{t('viewAll')}</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y max-h-[400px] overflow-y-auto scrollbar-none">
                    {recentItems.length === 0 ? (
                      <div className="p-6 text-center text-muted-foreground">
                        {t('noDbFound')}
                      </div>
                    ) : (
                      recentItems.map((item) => (
                        <Link
                          key={`${item.type}-${item.id ?? item._id}`}
                          to={item.itemUrl}
                          className="flex items-center hover:bg-muted/50 p-4 transition-colors"
                        >
                          <div className="h-14 w-14 rounded-md overflow-hidden flex-shrink-0 bg-muted mr-4 border">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = '/placeholder.svg';
                                }}
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center bg-muted">
                                <Package className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.name}</p>
                            <div className="flex items-center mt-1 gap-2">
                              <Badge variant="secondary" className="text-xs font-normal">
                                {item.brand}
                              </Badge>
                              <Badge variant="outline" className="text-xs font-normal">
                                {item.category || t('uncategorized')}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              <span className="font-medium text-primary">${item.price?.toFixed(2)}</span>
                              {' - '}
                              {t('addedDate')} {new Date(item.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="ml-2">
                            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default AdminDashboard;
